import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const Login = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          toast.success('Login successful!');
          navigate('/');
        } else {
          toast.error(result.error);
        }
      } else {
        const result = await register(formData.name, formData.email, formData.password);
        if (result.success) {
          toast.success('Registration successful!');
          navigate('/');
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-24">
      <div className="max-w-md w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-8">
            <Link to="/">
              <img src="https://customer-assets.emergentagent.com/job_gen-z-jewels-1/artifacts/8dp6l3ul_nova.png" alt="NOVA" className="h-14 w-auto mx-auto mb-2" />
            </Link>
            <p className="text-sm text-[#7A8A73]">
              {isLogin ? 'Welcome back' : 'Join the modern jewelry revolution'}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_0_rgba(45,59,38,0.08)]">
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setIsLogin(true)}
                data-testid="tab-login"
                className={`flex-1 py-3 rounded-full text-sm uppercase tracking-widest font-semibold transition-colors duration-300 ${
                  isLogin ? 'bg-[#C27054] text-white' : 'bg-[#E4E8DF] text-[#2D3B26]'
                }`}
              >
                Login
              </button>
              <button
                onClick={() => setIsLogin(false)}
                data-testid="tab-register"
                className={`flex-1 py-3 rounded-full text-sm uppercase tracking-widest font-semibold transition-colors duration-300 ${
                  !isLogin ? 'bg-[#C27054] text-white' : 'bg-[#E4E8DF] text-[#2D3B26]'
                }`}
              >
                Register
              </button>
            </div>

            <form onSubmit={handleSubmit} data-testid="auth-form" className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-[#2D3B26] mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="input-name"
                    required={!isLogin}
                    className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                    placeholder="Enter your name"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#2D3B26] mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="input-email"
                  required
                  className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2D3B26] mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  data-testid="input-password"
                  required
                  className="w-full px-4 py-3 bg-[#E4E8DF] border-none rounded-xl text-[#2D3B26] placeholder-[#7A8A73] focus:outline-none focus:ring-2 focus:ring-[#C27054]"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-button"
                className="w-full bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Please wait...' : isLogin ? 'Login' : 'Register'}
              </button>
            </form>

            {/* Social login divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[rgba(45,59,38,0.1)]"></div>
              <span className="text-[10px] uppercase tracking-widest text-[#7A8A73] font-semibold">or continue with</span>
              <div className="flex-1 h-px bg-[rgba(45,59,38,0.1)]"></div>
            </div>

            {/* Continue with Google */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              data-testid="google-login-button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 bg-white border border-[rgba(45,59,38,0.15)] text-[#2D3B26] rounded-full px-6 py-3.5 hover:bg-[#fafaf8] hover:border-[#C27054] transition-all duration-300 text-sm font-semibold"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 001 12c0 1.77.42 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <p className="text-[11px] text-center text-[#7A8A73] mt-4">
              By continuing, you agree to NOVA's Terms &amp; Privacy. New here? Google sign-in creates your NOVA account automatically.
            </p>
          </div>

          <p className="text-center text-sm text-[#7A8A73] mt-6">
            <Link to="/" className="hover:text-[#C27054] transition-colors">
              Back to Home
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;